import React, { useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Share2, TrendingUp, TrendingDown, Sparkles, RefreshCw, Edit3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AntisemitismOverview } from '../components/AntisemitismOverview';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { rassegne, articles, tierLabels, contentTypeLabels, dashboardKPIs, coverageByTier, sentimentOverTime, topSources, spokespersonMentions, executiveSummary, sentimentLabels } from '../lib/data';
function getSentimentColor(sentiment: string) {
  switch (sentiment) {
    case 'positive':
      return 'bg-sentiment-positive';
    case 'neutral':
      return 'bg-sentiment-neutral';
    case 'negative':
      return 'bg-sentiment-negative';
    default:
      return 'bg-muted';
  }
}
function getTierColor(tier: number | string) {
  switch (tier) {
    case 1:
      return 'bg-tier-1 text-white';
    case 2:
      return 'bg-tier-2 text-white';
    case 3:
      return 'bg-tier-3 text-white';
    case 'specialized':
      return 'bg-tier-special text-white';
    default:
      return 'bg-muted';
  }
}
function KPI({
  label,
  value,
  trend,
  accent = false
}: {
  label: string;
  value: string | number;
  trend?: number;
  accent?: boolean;
}) {
  return <div className="flex flex-col gap-2" data-id="element-1184">
      <div className="text-sm text-muted-foreground font-normal font-heading" data-id="element-1185">
        {label}
      </div>
      <div className={`${`text-3xl md:text-4xl font-light leading-none ${accent ? 'text-accent' : 'text-foreground'}`} gap-[0px]`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-1186">
        {value}
      </div>
      {trend !== undefined && <div className="flex items-center gap-1.5 text-sm bg-[#FFFFFF] font-normal" data-id="element-1187">
          {trend > 0 ? <TrendingUp className="h-3.5 w-3.5 text-sentiment-positive" data-id="element-1188" /> : <TrendingDown className="h-3.5 w-3.5 text-sentiment-negative" data-id="element-1189" />}
          <span className={trend > 0 ? 'text-sentiment-positive' : 'text-sentiment-negative'} data-id="element-1190">
            {trend > 0 ? '+' : ''}
            {trend}%
          </span>
          <span className="text-muted-foreground" data-id="element-1191">vs prec.</span>
        </div>}
    </div>;
}
export function RassegnaDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const rassegna = rassegne.find(r => r.id === id) || rassegne[0];
  const rassegnaArticles = articles;
  const topArticles = articles.filter(a => a.status === 'approved').slice(0, 5);
  return <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col gap-[0px]" data-id="element-1192">
      {/* Header + Tabs as one white block */}
      <div className="bg-card border-b border-border" data-id="element-1193">
        <div className="px-6 pt-6 md:pt-8 max-w-[1400px] mx-auto w-full" data-id="element-1194">
          <button onClick={() => navigate('/rassegne')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-id="element-1195">
            <ArrowLeft className="h-3.5 w-3.5" data-id="element-1196" />
            Rassegne
          </button>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6" data-id="element-1197">
            <div className="min-w-0" data-id="element-1198">
              <h1 className="text-xl md:text-2xl font-normal tracking-tight leading-tight mb-2" data-id="element-1199">
                {rassegna.name}
              </h1>
              <div className="text-sm text-muted-foreground font-normal" data-id="element-1200">
                {rassegna.dateRange}
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-shrink-0" data-id="element-1201">
              <Button variant="outline" className="rounded-full border-accent text-accent hover:bg-accent/10 hover:text-accent h-11 px-6 text-sm" data-id="element-1202">
                <Share2 className="h-4 w-4 md:mr-2" data-id="element-1203" />
                <span className="hidden md:inline" data-id="element-1204">Condividi</span>
              </Button>
              <Button className="rounded-full h-11 px-6 text-sm" data-id="element-1205">
                <Download className="h-4 w-4 md:mr-2" data-id="element-1206" />
                <span className="hidden md:inline" data-id="element-1207">Esporta PDF</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar - sticky */}
      <div className="border-b border-border sticky top-0 z-10 bg-card" data-id="element-1208">
        <div className="px-6 max-w-[1400px] mx-auto w-full flex items-end gap-0" data-id="element-1209">
          {[{
          value: 'overview',
          label: 'Panoramica'
        }, {
          value: 'analysis',
          label: 'Analisi'
        }, {
          value: 'articles',
          label: `Articoli`,
          count: rassegna.articleCount
        }].map(tab => <button key={tab.value} onClick={() => setActiveTab(tab.value)} className="relative px-5 py-3 text-sm transition-colors font-heading" style={{
          backgroundColor: activeTab === tab.value ? '#FFFFFF' : 'transparent',
          color: activeTab === tab.value ? 'var(--foreground)' : 'var(--muted-foreground)',
          fontWeight: activeTab === tab.value ? 500 : 400
        }} data-id="element-1210">
              {tab.label}
              {tab.count !== undefined && <span className="ml-2 text-xs" style={{
            color: 'var(--muted-foreground)'
          }} data-id="element-1211">
                  {tab.count}
                </span>}
              {activeTab === tab.value && <span className="absolute bottom-0 left-0 right-0" style={{
            height: '2px',
            backgroundColor: '#0187c3'
          }} data-id="element-1212" />}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <div data-id="element-1213">
        <TabsContent value="overview" className="m-0 p-6 max-w-[1400px] mx-auto space-y-8" data-id="element-1214">
          {/* KPI strip */}
          <section className="bg-card border border-border rounded-lg p-6 grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10" data-id="element-1215">
            <KPI label="Articoli totali" value={dashboardKPIs.totalArticles.value} trend={dashboardKPIs.totalArticles.trend} data-id="element-1216" />
            <KPI label="Score medio" value={dashboardKPIs.avgRelevanceScore.value.toFixed(1)} accent data-id="element-1217" />
            <KPI label="AVE totale" value={`€${(dashboardKPIs.totalAVE.value / 1000).toFixed(0)}k`} trend={dashboardKPIs.totalAVE.trend} data-id="element-1218" />
            <div className="flex flex-col gap-2" data-id="element-1219">
              <div className="text-sm text-muted-foreground font-normal font-heading" data-id="element-1220">
                Sentiment
              </div>
              <div className="flex items-baseline gap-2 leading-none" data-id="element-1221">
                <span className="text-2xl md:text-3xl font-light text-sentiment-positive" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-1222">
                  {dashboardKPIs.sentimentMix.positive}
                </span>
                <span className="text-sm text-muted-foreground font-light" data-id="element-1223">
                  /
                </span>
                <span className="text-xl text-muted-foreground font-light" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-1224">
                  {dashboardKPIs.sentimentMix.neutral}
                </span>
                <span className="text-sm text-muted-foreground font-light" data-id="element-1225">
                  /
                </span>
                <span className="text-xl text-sentiment-negative font-light" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-1226">
                  {dashboardKPIs.sentimentMix.negative}
                </span>
              </div>
              <div className="text-sm text-muted-foreground font-normal" data-id="element-1227">
                Positivi / neutri / negativi
              </div>
            </div>
          </section>

          {/* Executive Summary */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-1228">
            <div className="border-l-2 border-accent pl-5 md:pl-6" data-id="element-1229">
              <div className="flex items-center justify-between mb-3" data-id="element-1230">
                <div className="flex items-center gap-2 text-sm text-muted-foreground font-normal" data-id="element-1231">
                  <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-1232" />
                  <span className="font-heading" data-id="element-1233">Riepilogo AI</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-1234">
                  <Button variant="ghost" size="sm" className="h-9 px-3.5 text-sm text-muted-foreground" data-id="element-1235">
                    <RefreshCw className="h-3.5 w-3.5 md:mr-1.5" data-id="element-1236" />
                    <span className="hidden md:inline" data-id="element-1237">Rigenera</span>
                  </Button>
                  <Button variant="ghost" size="sm" className="h-9 px-3.5 text-sm text-muted-foreground" data-id="element-1238">
                    <Edit3 className="h-3.5 w-3.5 md:mr-1.5" data-id="element-1239" />
                    <span className="hidden md:inline" data-id="element-1240">Modifica</span>
                  </Button>
                </div>
              </div>
              <p className="text-base leading-relaxed text-foreground" data-id="element-1241">
                {executiveSummary}
              </p>
            </div>
          </section>

          {/* Top Articles */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-1242">
            <div className="flex items-baseline justify-between mb-5" data-id="element-1243">
              <h2 className="text-lg font-medium" data-id="element-1244">Top articoli</h2>
            </div>
            <div className="space-y-1" data-id="element-1245">
              {topArticles.map((article, i) => <Fragment key={article.id} data-id="element-1246">
                  {i > 0 && <Separator data-id="element-1247" />}
                  <button onClick={() => navigate(`/article/${article.id}`)} className="w-full text-left py-4 md:py-5 group hover:bg-muted/30 -mx-3 px-3 rounded-md transition-colors" data-id="element-1248">
                    <div className="flex items-start gap-4" data-id="element-1249">
                      <img src={article.sourceLogo} alt={article.source} className="w-9 h-9 rounded flex-shrink-0 mt-0.5" data-id="element-1250" />
                      <div className="flex-1 min-w-0" data-id="element-1251">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5 text-xs text-muted-foreground font-normal" data-id="element-1252">
                          <Badge className={`text-[10px] px-1.5 py-0 border-0 font-normal ${getTierColor(article.tier)}`} data-id="element-1253">
                            {tierLabels[article.tier]}
                          </Badge>
                          <span data-id="element-1254">{article.source}</span>
                          <span data-id="element-1255">·</span>
                          <span data-id="element-1256">
                            {new Date(article.date).toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'short'
                        })}
                          </span>
                        </div>
                        <h3 className="font-normal text-sm md:text-base leading-snug mb-2 line-clamp-2" data-id="element-1257">
                          {article.title}
                        </h3>
                        <div className="flex items-center gap-5 text-sm font-normal" data-id="element-1258">
                          <div className="flex items-baseline gap-1.5" data-id="element-1259">
                            <span className="font-light text-foreground" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1260">
                              {article.relevanceScore.toFixed(1)}
                            </span>
                            <span className="text-xs text-muted-foreground font-normal" data-id="element-1261">
                              score
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1.5 text-muted-foreground" data-id="element-1262">
                            <span style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1263">
                              €{(article.ave / 1000).toFixed(1)}k
                            </span>
                            <span className="text-xs" data-id="element-1264">AVE</span>
                          </div>
                          <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(article.sentiment)}`} title={sentimentLabels[article.sentiment]} data-id="element-1265" />
                        </div>
                      </div>
                    </div>
                  </button>
                </Fragment>)}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="analysis" className="m-0 p-6 max-w-[1400px] mx-auto space-y-8" data-id="element-1266">
          {/* Sentiment over time */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-1267">
            <h3 className="text-base font-medium mb-6" data-id="element-1268">Sentiment nel tempo</h3>
            <div className="h-64" data-id="element-1269">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1270">
                <AreaChart data={sentimentOverTime} margin={{
                top: 5,
                right: 0,
                bottom: 0,
                left: -20
              }} data-id="element-1271">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-1272" />
                  <XAxis dataKey="date" tick={{
                  fontSize: 12
                }} stroke="#8B8680" axisLine={false} tickLine={false} dy={10} data-id="element-1273" />
                  <YAxis tick={{
                  fontSize: 12
                }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-1274" />
                  <Area type="monotone" dataKey="positive" stackId="1" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1275" />
                  <Area type="monotone" dataKey="neutral" stackId="1" stroke="#64B5F6" fill="#64B5F6" fillOpacity={0.35} strokeWidth={1.5} data-id="element-1276" />
                  <Area type="monotone" dataKey="negative" stackId="1" stroke="#1A3A5C" fill="#1A3A5C" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1277" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Antisemitism three-framework overview */}
          <AntisemitismOverview articles={rassegnaArticles} heading="Antisemitismo rilevato nella rassegna" data-id="element-1278" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1279">
            {/* Coverage by Tier */}
            <section className="bg-card border border-border rounded-lg p-6" data-id="element-1280">
              <h3 className="text-base font-medium mb-6" data-id="element-1281">Copertura per tier</h3>
              <div className="space-y-4" data-id="element-1282">
                {coverageByTier.map(item => <div key={item.tier} data-id="element-1283">
                    <div className="flex items-center justify-between mb-2 text-sm" data-id="element-1284">
                      <span className="text-foreground font-normal" data-id="element-1285">
                        {item.tier}
                      </span>
                      <span className="text-muted-foreground font-normal" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1286">
                        {item.count}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-1287">
                      <div className="h-full bg-foreground/80 rounded-full" style={{
                    width: `${item.percentage}%`
                  }} data-id="element-1288" />
                    </div>
                  </div>)}
              </div>
            </section>

            {/* Top Sources */}
            <section className="bg-card border border-border rounded-lg p-6" data-id="element-1289">
              <h3 className="text-base font-medium mb-6" data-id="element-1290">Top testate</h3>
              <div className="space-y-4" data-id="element-1291">
                {topSources.map(source => <div key={source.name} className="flex items-center justify-between text-sm" data-id="element-1292">
                    <span className="text-foreground font-normal truncate pr-4" data-id="element-1293">
                      {source.name}
                    </span>
                    <div className="flex items-center gap-4 text-muted-foreground flex-shrink-0 font-normal" data-id="element-1294">
                      <span style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1295">
                        {source.articles} art.
                      </span>
                      <span className="text-foreground font-light w-8 text-right" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1296">
                        {source.avgScore.toFixed(1)}
                      </span>
                    </div>
                  </div>)}
              </div>
            </section>
          </div>

          {/* Spokespersons */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-1297">
            <h3 className="text-base font-medium mb-6" data-id="element-1298">Portavoce citati</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1299">
              {spokespersonMentions.map(person => <div key={person.name} className="flex items-center justify-between text-sm p-4 border border-border rounded-md bg-muted/10" data-id="element-1300">
                  <div className="min-w-0 pr-4" data-id="element-1301">
                    <div className="text-foreground font-normal truncate" data-id="element-1302">
                      {person.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 font-normal" data-id="element-1303">
                      {person.role}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1304">
                    <span className="font-light text-lg" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1305">
                      {person.mentions}
                    </span>
                    <div className={`w-2 h-2 rounded-full ${getSentimentColor(person.avgSentiment)}`} data-id="element-1306" />
                  </div>
                </div>)}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="articles" className="m-0 p-6 max-w-[1400px] mx-auto" data-id="element-1307">
          <div className="bg-card border border-border rounded-lg overflow-hidden" data-id="element-1308">
            {/* Mobile */}
            <div className="md:hidden divide-y divide-border" data-id="element-1309">
              {rassegnaArticles.map(article => <button key={article.id} onClick={() => navigate(`/article/${article.id}`)} className="w-full text-left py-5 hover:bg-muted/30 px-4 transition-colors" data-id="element-1310">
                  <div className="flex items-center gap-2 mb-1.5 text-sm text-muted-foreground font-normal" data-id="element-1311">
                    <img src={article.sourceLogo} alt="" className="w-4 h-4 rounded-sm" data-id="element-1312" />
                    <span data-id="element-1313">{article.source}</span>
                    <span data-id="element-1314">·</span>
                    <span data-id="element-1315">
                      {new Date(article.date).toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'short'
                  })}
                    </span>
                  </div>
                  <h3 className="font-normal text-base leading-snug mb-2 line-clamp-2" data-id="element-1316">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-4 text-sm font-normal" data-id="element-1317">
                    <span className="font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1318">
                      {article.relevanceScore.toFixed(1)}
                    </span>
                    <span className="text-muted-foreground font-normal" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1319">
                      €{(article.ave / 1000).toFixed(1)}k
                    </span>
                    <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(article.sentiment)}`} data-id="element-1320" />
                  </div>
                </button>)}
            </div>

            {/* Desktop */}
            <div className="hidden md:block" data-id="element-1321">
              <Table data-id="element-1322">
                <TableHeader data-id="element-1323">
                  <TableRow className="hover:bg-transparent" data-id="element-1324">
                    <TableHead className="w-[100px] text-sm font-heading" data-id="element-1325">
                      Data
                    </TableHead>
                    <TableHead className="text-sm font-heading" data-id="element-1326">
                      Testata
                    </TableHead>
                    <TableHead className="w-[90px] text-sm font-heading" data-id="element-1327">
                      Tier
                    </TableHead>
                    <TableHead className="text-sm font-heading" data-id="element-1328">
                      Titolo
                    </TableHead>
                    <TableHead className="w-[150px] text-sm font-heading" data-id="element-1329">
                      Tipologia
                    </TableHead>
                    <TableHead className="text-right w-[80px] text-sm font-heading" data-id="element-1330">
                      Score
                    </TableHead>
                    <TableHead className="text-right w-[80px] text-sm font-heading" data-id="element-1331">
                      AVE
                    </TableHead>
                    <TableHead className="w-[60px]" data-id="element-1332"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="element-1333">
                  {rassegnaArticles.map(article => <TableRow key={article.id} className="cursor-pointer font-normal" onClick={() => navigate(`/article/${article.id}`)} data-id="element-1334">
                      <TableCell className="text-[15px] text-muted-foreground whitespace-nowrap py-4 font-normal" data-id="element-1335">
                        {new Date(article.date).toLocaleDateString('it-IT')}
                      </TableCell>
                      <TableCell className="py-4 font-normal" data-id="element-1336">
                        <div className="flex items-center gap-2" data-id="element-1337">
                          <img src={article.sourceLogo} alt="" className="w-5 h-5 rounded-sm" data-id="element-1338" />
                          <span className="text-[15px]" data-id="element-1339">{article.source}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4" data-id="element-1340">
                        <Badge className={`text-[10px] px-1.5 py-0 border-0 font-normal ${getTierColor(article.tier)}`} data-id="element-1341">
                          {tierLabels[article.tier]}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[400px] py-4" data-id="element-1342">
                        <div className="text-[15px] font-normal truncate" data-id="element-1343">
                          {article.title}
                        </div>
                      </TableCell>
                      <TableCell className="text-[15px] text-muted-foreground py-4 font-normal" data-id="element-1344">
                        {contentTypeLabels[article.contentType]}
                      </TableCell>
                      <TableCell className="text-right font-light text-[15px] py-4" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1345">
                        {article.relevanceScore.toFixed(1)}
                      </TableCell>
                      <TableCell className="text-right text-[15px] text-muted-foreground py-4" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1346">
                        €{(article.ave / 1000).toFixed(1)}k
                      </TableCell>
                      <TableCell className="py-4" data-id="element-1347">
                        <div className={`w-2 h-2 rounded-full ${getSentimentColor(article.sentiment)} mx-auto`} data-id="element-1348" />
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </div>
    </Tabs>;
}